import { useState, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import { createFileUploader, destroyUploader } from '@/lib/files/uppy-upload';
import { validateFilesForUpload } from '@/lib/files/image-validation';

/**
 * File attachment state during upload lifecycle
 */
export interface AttachedFileWithState {
  file: File;
  uploadState: 'pending' | 'uploading' | 'completed' | 'failed';
  uploadProgress?: number;
  fileId?: string;
  refId?: string;
  downloadUrl?: string;
  thumbnailUrl?: string;
  error?: string;
}

export interface UseFileUploadOptions {
  projectId?: string;
  sessionId: string | null;
  devContainerId: string;
}

export interface UseFileUploadReturn {
  attachedFiles: AttachedFileWithState[];
  setAttachedFiles: React.Dispatch<React.SetStateAction<AttachedFileWithState[]>>;
  fileInputRef: React.RefObject<HTMLInputElement>;
  handleFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  handleFilesDropped: (files: File[]) => Promise<void>;
  clearAttachedFiles: () => void;
}

/**
 * Hook for handling file uploads in the agent chat
 * Manages file state, validation, and upload to R2
 */
export function useFileUpload({
  projectId,
  sessionId,
  devContainerId,
}: UseFileUploadOptions): UseFileUploadReturn {
  const [attachedFiles, setAttachedFiles] = useState<AttachedFileWithState[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * Upload a single file immediately using Uppy
   */
  const uploadFileImmediately = useCallback(
    async (file: File): Promise<AttachedFileWithState> => {
      try {
        console.log('[useFileUpload] Starting immediate upload for:', file.name);

        const uppy = createFileUploader({
          userMessage: '',
          source: 'chat',
          projectId,
          sessionId: sessionId || undefined,
          devContainerId: devContainerId || '',
          onProgress: (_file, progress) => {
            console.log(`[useFileUpload] Upload progress for ${file.name}: ${progress.toFixed(0)}%`);
            setAttachedFiles((prev) =>
              prev.map((f) =>
                f.file.name === file.name && f.file.size === file.size
                  ? { ...f, uploadProgress: progress }
                  : f
              )
            );
          },
        });

        uppy.addFile({
          name: file.name,
          type: file.type,
          data: file,
        });

        const result = await new Promise<{
          fileId: string;
          refId: string;
          intent: string;
          downloadUrl: string;
          thumbnailUrl: string;
        }>((resolve, reject) => {
          uppy.on('complete', async (result) => {
            if (result.successful && result.successful.length > 0) {
              const uploadedFile = result.successful[0];

              // Wait for upload complete callback to finish
              let attempts = 0;
              while (!uploadedFile.meta.uploadCompleteFinished && attempts < 100) {
                await new Promise((r) => setTimeout(r, 100));
                attempts++;
              }

              const uploadResult = {
                fileId: uploadedFile.meta.fileId as string,
                refId: uploadedFile.meta.refId as string,
                intent: uploadedFile.meta.intent as string,
                downloadUrl: uploadedFile.meta.downloadUrl as string,
                thumbnailUrl: uploadedFile.meta.thumbnailUrl as string,
              };

              console.log('[useFileUpload] File upload complete:', uploadResult);
              resolve(uploadResult);
            } else {
              reject(new Error('Upload failed'));
            }
          });

          uppy.on('error', (error) => {
            reject(error);
          });

          uppy.upload();
        });

        destroyUploader(uppy);

        return {
          file,
          uploadState: 'completed',
          uploadProgress: 100,
          fileId: result.fileId,
          refId: result.refId,
          downloadUrl: result.downloadUrl,
          thumbnailUrl: result.thumbnailUrl,
        };
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        console.error('[useFileUpload] File upload error:', error);

        // Show toast for file type restriction errors
        if (errorMessage.includes('You can only upload')) {
          toast.error(`${file.name}: File type not supported`, { duration: 4000 });
        } else {
          toast.error(`${file.name}: Upload failed`, { duration: 4000 });
        }

        return {
          file,
          uploadState: 'failed',
          error: errorMessage,
        };
      }
    },
    [projectId, sessionId, devContainerId]
  );

  /**
   * Handle file input change (from file picker)
   */
  const handleFileInputChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        const allFiles = Array.from(files);
        console.log('[useFileUpload] Files attached:', allFiles.map((f) => f.name).join(', '));

        // Validate files before upload (async - checks magic bytes)
        const { valid: newFiles, invalid } = await validateFilesForUpload(allFiles);

        // Show toast for rejected files
        if (invalid.length > 0) {
          invalid.forEach(({ file, reason }) => {
            toast.error(`${file.name}: ${reason}`, { duration: 4000 });
          });
        }

        // Only proceed with valid files
        if (newFiles.length === 0) {
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
          return;
        }

        const filesWithState: AttachedFileWithState[] = newFiles.map((file) => ({
          file,
          uploadState: 'uploading' as const,
          uploadProgress: 0,
        }));
        setAttachedFiles((prev) => [...prev, ...filesWithState]);

        const uploadPromises = newFiles.map((file) => uploadFileImmediately(file));
        const uploadedFiles = await Promise.all(uploadPromises);

        // Filter out failed uploads (e.g., file type restrictions)
        const successfulUploads = uploadedFiles.filter((f) => f.uploadState !== 'failed');

        setAttachedFiles((prev) => {
          const filtered = prev.filter(
            (f) => !newFiles.some((nf) => nf.name === f.file.name && nf.size === f.file.size)
          );
          return [...filtered, ...successfulUploads];
        });
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [uploadFileImmediately]
  );

  /**
   * Handle files dropped via drag and drop
   */
  const handleFilesDropped = useCallback(
    async (droppedFiles: File[]) => {
      console.log('[useFileUpload] Files dropped:', droppedFiles.map((f) => f.name).join(', '));

      // Validate files before upload (async - checks magic bytes)
      const { valid: validFiles, invalid } = await validateFilesForUpload(droppedFiles);

      // Show toast for rejected files
      if (invalid.length > 0) {
        invalid.forEach(({ file, reason }) => {
          toast.error(`${file.name}: ${reason}`, { duration: 4000 });
        });
      }

      // Only proceed with valid files
      if (validFiles.length === 0) {
        return;
      }

      const filesWithState: AttachedFileWithState[] = validFiles.map((file) => ({
        file,
        uploadState: 'uploading' as const,
        uploadProgress: 0,
      }));
      setAttachedFiles((prev) => [...prev, ...filesWithState]);

      const uploadPromises = validFiles.map((file) => uploadFileImmediately(file));
      const uploadedFiles = await Promise.all(uploadPromises);

      // Filter out failed uploads (e.g., file type restrictions)
      const successfulUploads = uploadedFiles.filter((f) => f.uploadState !== 'failed');

      setAttachedFiles((prev) => {
        const filtered = prev.filter(
          (f) => !validFiles.some((df) => df.name === f.file.name && df.size === f.file.size)
        );
        return [...filtered, ...successfulUploads];
      });
    },
    [uploadFileImmediately]
  );

  /**
   * Clear all attached files
   */
  const clearAttachedFiles = useCallback(() => {
    setAttachedFiles([]);
  }, []);

  return {
    attachedFiles,
    setAttachedFiles,
    fileInputRef,
    handleFileInputChange,
    handleFilesDropped,
    clearAttachedFiles,
  };
}
